/**
 * Utilitaires de détection d'appareil mobile/PC
 * Utilise plusieurs méthodes combinées pour une détection fiable
 */

/**
 * Détecte si l'appareil est mobile basé sur le User Agent
 * @returns {boolean} true si l'appareil est mobile
 */
export function isMobileByUserAgent() {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileKeywords = [
    'android', 'iphone', 'ipad', 'ipod', 'blackberry', 
    'windows phone', 'mobile', 'tablet'
  ];
  
  return mobileKeywords.some(keyword => userAgent.includes(keyword));
}

/**
 * Détecte si l'appareil supporte le tactile
 * @returns {boolean} true si l'appareil supporte le tactile
 */
export function hasTouchSupport() {
  if (typeof window === 'undefined') return false;
  
  return 'ontouchstart' in window || 
         navigator.maxTouchPoints > 0 || 
         navigator.msMaxTouchPoints > 0;
}

/**
 * Détecte si l'appareil est tactile uniquement (pas de souris)
 * @returns {boolean} true si l'appareil est tactile uniquement
 */
export function isTouchOnly() {
  if (typeof window === 'undefined') return false;
  
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}

/**
 * Détecte si l'appareil a une souris
 * @returns {boolean} true si l'appareil a une souris
 */
export function hasMouse() {
  if (typeof window === 'undefined') return false;
  
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

/**
 * Détecte le type d'appareil complet avec toutes les informations
 * @returns {Object} Informations détaillées sur l'appareil
 */
export function detectDeviceType() {
  if (typeof window === 'undefined') {
    return {
      isMobile: false,
      isDesktop: true,
      hasTouch: false,
      hasMouse: false,
      isTouchOnly: false,
      userAgent: '',
      deviceType: 'unknown'
    };
  }

  const userAgent = navigator.userAgent.toLowerCase();
  const hasTouch = hasTouchSupport();
  const isTouchOnlyDevice = isTouchOnly();
  const hasMouseDevice = hasMouse();
  
  const mobileKeywords = ['android', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];
  const hasMobileUA = mobileKeywords.some(keyword => userAgent.includes(keyword));
  
  // Détection tablette spécifique
  const isTablet = userAgent.includes('ipad') || 
                   (userAgent.includes('android') && !userAgent.includes('mobile')) ||
                   userAgent.includes('tablet');
  
  // Détection mobile
  const isMobile = hasMobileUA || isTouchOnlyDevice || isTablet;
  
  // Détermination du type d'appareil
  let deviceType = 'desktop';
  if (isTablet) deviceType = 'tablet';
  else if (isMobile) deviceType = 'mobile';
  
  return {
    isMobile,
    isDesktop: !isMobile,
    hasTouch,
    hasMouse: hasMouseDevice,
    isTouchOnly: isTouchOnlyDevice,
    isTablet,
    userAgent,
    deviceType
  };
}

/**
 * Détecte si l'appareil est mobile (méthode simplifiée)
 * @returns {boolean} true si l'appareil est mobile
 */
export function isMobile() {
  const deviceInfo = detectDeviceType();
  return deviceInfo.isMobile;
}

/**
 * Détecte si l'appareil est desktop (méthode simplifiée)
 * @returns {boolean} true si l'appareil est desktop
 */
export function isDesktop() {
  const deviceInfo = detectDeviceType();
  return deviceInfo.isDesktop;
}

/**
 * Détecte si l'appareil est une tablette
 * @returns {boolean} true si l'appareil est une tablette
 */
export function isTablet() {
  const deviceInfo = detectDeviceType();
  return deviceInfo.isTablet;
}

/**
 * Écoute les changements de type d'appareil (responsive)
 * @param {Function} callback - Fonction appelée lors du changement
 * @returns {Function} Fonction pour arrêter l'écoute
 */
export function onDeviceTypeChange(callback) {
  if (typeof window === 'undefined') return () => {};
  
  const mediaQuery = window.matchMedia('(hover: none) and (pointer: coarse)');
  
  const handleChange = () => {
    callback(detectDeviceType());
  };
  
  mediaQuery.addEventListener('change', handleChange);
  
  return () => {
    mediaQuery.removeEventListener('change', handleChange);
  };
} 